import React, { useEffect, useState } from 'react'
import { Table, Tag, Button, Modal, Form, Input, Select, Popconfirm, message, AutoComplete, Checkbox, Space, Divider, Avatar } from 'antd'
import { PlusOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons'
import { users } from '../../api'
import { useAuth } from '../../store/auth'
import api from '../../api'
import dayjs from 'dayjs'

const ROLE_MAP: Record<string, { label: string; color: string }> = {
  super_admin: { label: '超级管理员', color: 'magenta' },
  admin: { label: '管理员', color: 'orange' },
  operator: { label: '运营', color: 'blue' },
}

export default function UsersPage() {
  const { user: me } = useAuth()
  const [list, setList] = useState<any[]>([])
  const [loginLogs, setLoginLogs] = useState<any[]>([])
  const [usageLogs, setUsageLogs] = useState<any[]>([])
  const [modal, setModal] = useState(false)
  const [form] = Form.useForm()
  const [feishuOptions, setFeishuOptions] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<'list' | 'login' | 'usage'>('list')

  const [permModal, setPermModal] = useState<any>(null)
  const [permModules, setPermModules] = useState<any[]>([])
  const [permGranted, setPermGranted] = useState<string[]>([])

  const isSuper = me?.role === 'super_admin'

  const loadList = () => users.list().then((r: any) => setList(r))

  useEffect(() => {
    loadList().catch(() => {})
    users.loginLogs().then((r: any) => setLoginLogs(r)).catch(() => {})
    users.usageLogs().then((r: any) => setUsageLogs(r)).catch(() => {})
    users.modules().then((r: any) => setPermModules(r)).catch(() => {})
  }, [])

  const searchFeishu = async (keyword: string) => {
    if (!keyword) return
    const r: any = await api.get('/users/feishu-search', { params: { q: keyword } }).catch(() => ({ results: [] }))
    setFeishuOptions((r.results || []).map((u: any) => ({
      value: u.open_id, label: `${u.name}（${u.open_id}）`, name: u.name,
    })))
  }

  const handleCreate = async (values: any) => {
    await users.create(values)
    await loadList()
    setModal(false)
    form.resetFields()
    message.success('创建成功')
  }

  const updateRole = async (id: number, role: string) => {
    await users.update(id, { role })
    setList(prev => prev.map(u => u.id === id ? { ...u, role } : u))
    message.success('角色已更新')
  }

  const toggleStatus = async (id: number, status: number) => {
    await users.update(id, { status: status === 1 ? 0 : 1 })
    setList(prev => prev.map(u => u.id === id ? { ...u, status: status === 1 ? 0 : 1 } : u))
  }

  const openPermissions = async (user: any) => {
    const r: any = await users.permissions(user.id)
    setPermGranted(r.granted || [])
    setPermModal(user)
  }

  const savePermissions = async () => {
    if (!permModal) return
    await users.setPermissions(permModal.id, permGranted)
    message.success('权限已保存')
    setPermModal(null)
  }

  const userColumns = [
    {
      title: '用户', dataIndex: 'name', width: 180,
      render: (v: string, r: any) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} style={{ background: '#536DFE' }} />
          <div>
            <div style={{ fontWeight: 500 }}>{v}</div>
            <div style={{ fontSize: 11, color: '#B9B9B9' }}>{r.feishu_open_id ? r.feishu_open_id.slice(0, 16) + '...' : '-'}</div>
          </div>
        </Space>
      )
    },
    {
      title: '角色', dataIndex: 'role', width: 140,
      render: (v: string, r: any) => {
        if (!isSuper || r.role === 'super_admin' || r.id === me?.id) {
          return <Tag color={ROLE_MAP[v]?.color}>{ROLE_MAP[v]?.label}</Tag>
        }
        return (
          <Select size="small" value={v} style={{ width: 110 }}
            onChange={(val) => updateRole(r.id, val)}
            options={[
              { value: 'admin', label: '管理员' },
              { value: 'operator', label: '运营' },
            ]} />
        )
      }
    },
    { title: '状态', dataIndex: 'status', width: 80,
      render: (v: number) => <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '禁用'}</Tag> },
    { title: '最后登录', dataIndex: 'last_login', width: 130,
      render: (v: string) => v ? dayjs(v).format('MM-DD HH:mm') : '-' },
    { title: '累计时长', dataIndex: 'total_login_duration', width: 100,
      render: (v: number) => v ? `${Math.round(v / 60)}分钟` : '-' },
    {
      title: '操作', width: 180,
      render: (_: any, r: any) => isSuper && r.role !== 'super_admin' && r.id !== me?.id ? (
        <Space size="small">
          <Button size="small" type="link" icon={<SettingOutlined />} onClick={() => openPermissions(r)}>权限</Button>
          <Popconfirm title={r.status ? '确认禁用？' : '确认启用？'} onConfirm={() => toggleStatus(r.id, r.status)}>
            <Button size="small" type="link" danger={!!r.status}>{r.status ? '禁用' : '启用'}</Button>
          </Popconfirm>
        </Space>
      ) : null
    },
  ]

  const loginLogColumns = [
    { title: '用户', dataIndex: 'user_name' },
    { title: '登录时间', dataIndex: 'login_at', render: (v: string) => dayjs(v).format('MM-DD HH:mm:ss') },
    { title: '时长(分)', dataIndex: 'duration_minutes' },
    { title: 'IP', dataIndex: 'ip' },
  ]

  const usageLogColumns = [
    { title: '用户', dataIndex: 'user_name' },
    { title: '时间', dataIndex: 'created_at', render: (v: string) => dayjs(v).format('MM-DD HH:mm:ss') },
    { title: '模块', dataIndex: 'module' },
    { title: '行为', dataIndex: 'action' },
    { title: '详情', dataIndex: 'detail', ellipsis: true },
  ]

  const groupedModules = permModules.reduce((acc: any, m: any) => {
    const key = m.key.split('.')[0]
    if (!acc[key]) acc[key] = []
    acc[key].push(m)
    return acc
  }, {})

  const GROUP_LABEL: Record<string, string> = {
    home: '首页', dashboard: '数据看板',
    tools: '运营工具', users: '用户管理', help: '常用文档中心',
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#4A4A4A' }}>👥 用户管理</div>
        <div style={{ color: '#B9B9B9', fontSize: 13, marginTop: 4 }}>账号、角色、权限与日志</div>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #E8EAFC' }}>
        {/* Tab */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #F0F0F0' }}>
          {[
            { key: 'list', label: `用户列表 (${list.length})` },
            { key: 'login', label: '登录日志' },
            { key: 'usage', label: '使用日志' },
          ].map(t => {
            const active = activeTab === t.key
            return (
              <div key={t.key} onClick={() => setActiveTab(t.key as any)}
                style={{
                  padding: '10px 20px', cursor: 'pointer', fontWeight: active ? 600 : 500,
                  color: active ? '#536DFE' : '#6E6E6E',
                  borderBottom: `2px solid ${active ? '#536DFE' : 'transparent'}`, marginBottom: -1,
                }}>{t.label}</div>
            )
          })}
        </div>

        {activeTab === 'list' && (
          <>
            {isSuper && (
              <Button icon={<PlusOutlined />} type="primary" onClick={() => setModal(true)} style={{ marginBottom: 12 }}>
                新增用户
              </Button>
            )}
            <Table size="middle" dataSource={list} columns={userColumns} rowKey="id" pagination={{ pageSize: 15 }} />
          </>
        )}
        {activeTab === 'login' && (
          <Table size="middle" dataSource={loginLogs} columns={loginLogColumns} rowKey="id" pagination={{ pageSize: 20 }} />
        )}
        {activeTab === 'usage' && (
          <Table size="middle" dataSource={usageLogs} columns={usageLogColumns} rowKey="id" pagination={{ pageSize: 20 }} />
        )}
      </div>

      <Modal title="新增用户" open={modal} onCancel={() => setModal(false)} onOk={() => form.submit()} destroyOnClose>
        <Form form={form} onFinish={handleCreate} layout="vertical">
          <Form.Item name="feishu_open_id" label="飞书账号" rules={[{ required: true, message: '请搜索并选择飞书用户' }]}
            extra="输入姓名搜索飞书用户">
            <AutoComplete
              options={feishuOptions}
              onSearch={searchFeishu}
              onSelect={(val: any, opt: any) => { form.setFieldValue('name', opt.name); form.setFieldValue('feishu_open_id', val) }}
              placeholder="输入姓名搜索..."
            />
          </Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="role" label="角色" initialValue="operator" rules={[{ required: true }]}>
            <Select options={[{ value: 'admin', label: '管理员' }, { value: 'operator', label: '运营' }]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={`权限配置 - ${permModal?.name || ''}`} open={!!permModal}
        onCancel={() => setPermModal(null)} onOk={savePermissions} width={600} destroyOnClose>
        <div style={{ color: '#999', fontSize: 12, marginBottom: 12 }}>
          勾选该用户可访问的模块。超管自动拥有所有权限。
        </div>
        <Space wrap>
          <Button size="small" onClick={() => setPermGranted(permModules.map(m => m.key))}>全选</Button>
          <Button size="small" onClick={() => setPermGranted([])}>清空</Button>
        </Space>
        <Divider style={{ margin: '12px 0' }} />
        {Object.keys(groupedModules).map(g => (
          <div key={g} style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#4A4A4A' }}>{GROUP_LABEL[g] || g}</div>
            <Checkbox.Group
              value={permGranted}
              onChange={(vals) => {
                const others = permGranted.filter(k => k.split('.')[0] !== g)
                setPermGranted([...others, ...(vals as string[])])
              }}
              options={groupedModules[g].map((m: any) => ({
                value: m.key,
                label: m.label.includes('/') ? m.label.split('/')[1] : m.label,
              }))}
            />
          </div>
        ))}
      </Modal>
    </div>
  )
}
